import TimeField from "@/core/components/Field/TimeField";
import { DatePicker } from "@/core/components/DatePicker/DatePicker";
import { useMediaQuery } from "@mui/material";
import { Box, useTheme } from "@mui/system";
import { FieldProps } from "@/core/components/Field";
import { Controller, useFormContext } from "react-hook-form";
import {
  DateTimeValidation,
  getDateTimeValidations,
} from "@/core/utils/dateTimeValidations";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";
import getFlatFormValue from "@/core/utils/getFlatFormValue";
import ErrorMessage from "@/core/components/ErrorMessage/ErrorMessage";
import { useFieldError } from "@/core/hooks/useFieldError/useFieldError";
import { FieldName } from "@/components/custom/AlternativeTransportation/Transportations/ManualTransports/ManualTransportInput/fields";

type DateTimeFieldProps = FieldProps & {
  onChange?: (newValue: string) => void;
};

const GenericDateTimeField = ({
  field,
  initialValue = new Date(),
  onChange,
}: DateTimeFieldProps) => {
  const { properties } = field;
  const { disableFutureDates } = properties;
  const isArrivalDateTime: boolean =
    field.key.indexOf(FieldName.ManualArrivalDateTime) > -1;
  const futurePeriod = isArrivalDateTime ? -14 : -13;
  const {
    watch,
    formState: { errors },
  } = useFormContext();
  const formValues = watch();

  const { hasError, errorMessage, errorTranslationKey } = useFieldError(
    errors,
    field
  );

  const validations = getDateTimeValidations(properties);
  const requiredValidationVariables = validations
    .filter((validation) => validation.requiredVariable)
    .map((validation) => validation.requiredVariable)
    .join(",");
  const errorMessages = validations.reduce(
    (acc: any, validation: DateTimeValidation) => {
      acc[validation.validationName] = t(
        `validate.${errorTranslationKey}.${validation.validationName}.message`
      );
      return acc;
    },
    {}
  );

  const { result }: any = useGetPreviousAnswers(requiredValidationVariables);

  const validateDateTime = (value: string): boolean | string => {
    // validate only if all compared fields are already filled
    const shouldValidate = validations.every((validation) =>
      !validation.requiredVariable
        ? getFlatFormValue(formValues, validation.compareWith)
        : true
    );
    if (!shouldValidate) return true;

    let failedValidation = "";
    const fieldIsValid = validations.every((validation) => {
      const comparedValue = validation.requiredVariable
        ? result[validation.requiredVariable]
        : getFlatFormValue(formValues, validation.compareWith);
      if (!comparedValue) return true;

      const validationPassed = validation.validate(value, comparedValue);
      if (!validationPassed) failedValidation = validation.validationName;

      return validationPassed;
    });

    return fieldIsValid || errorMessages[failedValidation];
  };

  const theme = useTheme();
  const isDesktop: boolean = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <Controller
      name={field.key}
      rules={{ validate: (value) => validateDateTime(value) }}
      render={() => (
        <Box id={field.key} mt={2}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isDesktop ? "row" : "column",
            }}
          >
            {initialValue && (
              <>
                <DatePicker
                  value={initialValue}
                  isFullSize={!isDesktop}
                  onPreviousDateSelect={onChange}
                  onNextDateSelect={onChange}
                  futurePeriod={futurePeriod}
                  disableFutureDates={disableFutureDates}
                />
                <Box m={1} />
                <TimeField
                  field={field}
                  initialValue={initialValue}
                  displayError={false}
                />
              </>
            )}
          </Box>
          {hasError && <ErrorMessage text={t(errorMessage)} />}
        </Box>
      )}
    />
  );
};

export default GenericDateTimeField;
